import { supabase } from '@/lib/supabase'

export type SignalSide = 'long' | 'short'

export type OpportunitySignals = {
  opportunityId: number
  side: SignalSide
  tfMandante: string
  tfSuperior: string | null
  entryMin: number | null
  entryMax: number | null
  entryMid: number | null
  stopPrice: number | null
  stopAggressive: number | null
  stopConservative: number | null
  tp1: number | null
  tp2: number | null
  tp3: number | null
  tp1Hit: boolean | null
  tp2Hit: boolean | null
  tp3Hit: boolean | null
  stopHit: boolean | null
}

export type OpportunitySignalsOutcome = {
  opportunityId: number
  resultType: 'tp1' | 'tp2' | 'tp3' | 'stop' | 'entry_missed'
  tp1Hit: boolean
  tp2Hit: boolean
  tp3Hit: boolean
  stopHit: boolean
  entryMin: number | null
  entryMax: number | null
  entryMid: number | null
  stopPrice: number | null
  tp1: number | null
  tp2: number | null
  tp3: number | null
  closedAt: string
}

export type OpportunityEntryZoneFlags = {
  inEntryZone: boolean
  hasEntryZone: boolean
}

export type OpportunityMultiTfState = {
  state: 'ok' | 'warning' | 'error'
  parentTf: string | null
}

export type OpportunityMultiTfDirectionConflict = {
  hasConflict: boolean
  higherTf: string | null
}

// Sinais completos (entrada, alvos, stops e progresso) para uma oportunidade
export async function fetchSignalsForOpportunity(opportunityId: number): Promise<OpportunitySignals | null> {
  const { data, error } = await supabase
    .rpc('compute_signals_full_for_opportunities', { p_ids: [opportunityId] })

  if (error) {
    console.error('compute_signals_full_for_opportunities error', error)
    return null
  }

  const row = Array.isArray(data) && data.length > 0 ? (data as any[])[0] : null
  if (!row) return null

  return {
    opportunityId: row.opportunity_id,
    side: row.side,
    tfMandante: row.tf_mandante,
    tfSuperior: row.tf_superior ?? null,
    entryMin: row.entry_min,
    entryMax: row.entry_max,
    entryMid: row.entry_mid,
    stopPrice: row.stop_price,
    stopAggressive: row.stop_aggressive ?? row.stop_price ?? null,
    stopConservative: row.stop_conservative ?? row.stop_price ?? null,
    tp1: row.tp1,
    tp2: row.tp2,
    tp3: row.tp3,
    tp1Hit: typeof row.tp1_hit === 'boolean' ? row.tp1_hit : null,
    tp2Hit: typeof row.tp2_hit === 'boolean' ? row.tp2_hit : null,
    tp3Hit: typeof row.tp3_hit === 'boolean' ? row.tp3_hit : null,
    stopHit: typeof row.stop_hit === 'boolean' ? row.stop_hit : null,
  }
}

// Sinais básicos (sem progresso) em batch para lista de oportunidades
export async function fetchSignalsForOpportunities(opportunityIds: number[]): Promise<Record<string, OpportunitySignals>> {
  const map: Record<string, OpportunitySignals> = {}
  const ids = Array.from(new Set(opportunityIds.filter((id) => Number.isFinite(id))))
  if (ids.length === 0) return map

  const { data, error } = await supabase
    .rpc('compute_signals_for_opportunities', { p_ids: ids })

  if (error) {
    console.error('compute_signals_for_opportunities error', error)
    return map
  }

  const rows = Array.isArray(data) ? data : []

  for (const row of rows as any[]) {
    if (!row) continue
    const key = String(row.opportunity_id)
    map[key] = {
      opportunityId: row.opportunity_id,
      side: row.side,
      tfMandante: row.tf_mandante,
      tfSuperior: row.tf_superior ?? null,
      entryMin: row.entry_min,
      entryMax: row.entry_max,
      entryMid: row.entry_mid,
      stopPrice: row.stop_price,
      stopAggressive: row.stop_aggressive ?? row.stop_price ?? null,
      stopConservative: row.stop_conservative ?? row.stop_price ?? null,
      tp1: row.tp1,
      tp2: row.tp2,
      tp3: row.tp3,
      tp1Hit: null,
      tp2Hit: null,
      tp3Hit: null,
      stopHit: null,
    }
  }

  return map
}

// Conflito de dire��o entre TF atual e TFs maiores
export async function fetchSignalsMultiTfDirectionConflicts(
  opportunityIds: number[],
): Promise<Record<string, OpportunityMultiTfDirectionConflict>> {
  const map: Record<string, OpportunityMultiTfDirectionConflict> = {}
  const ids = Array.from(new Set(opportunityIds.filter((id) => Number.isFinite(id))))
  if (ids.length === 0) return map

  const { data, error } = await supabase
    .rpc('compute_signals_multitf_direction_conflict', { p_ids: ids })

  if (error) {
    console.error('compute_signals_multitf_direction_conflict error', error)
    return map
  }

  const rows = Array.isArray(data) ? data : []

  for (const row of rows as any[]) {
    if (!row) continue
    const key = String(row.opportunity_id)
    map[key] = {
      hasConflict: !!row.has_conflict,
      higherTf: row.higher_tf ?? null,
    }
  }

  return map
}

// Estado multi-TF (ok/warning/error) para uma lista de oportunidades
export async function fetchSignalsMultiTfState(
  opportunityIds: number[],
): Promise<Record<string, OpportunityMultiTfState>> {
  const map: Record<string, OpportunityMultiTfState> = {}
  const ids = Array.from(new Set(opportunityIds.filter((id) => Number.isFinite(id))))
  if (ids.length === 0) return map

  const { data, error } = await supabase
    .rpc('compute_signals_multitf_state', { p_ids: ids })

  if (error) {
    console.error('compute_signals_multitf_state error', error)
    return map
  }

  const rows = Array.isArray(data) ? data : []

  for (const row of rows as any[]) {
    if (!row) continue
    const key = String(row.opportunity_id)
    const rawState = String(row.state || 'ok').toLowerCase()
    const state: OpportunityMultiTfState['state'] =
      rawState === 'warning' || rawState === 'error' ? rawState : 'ok'
    map[key] = {
      state,
      parentTf: row.parent_tf ?? null,
    }
  }

  return map
}

// Zona de entrada (dentro/fora) para uma lista de oportunidades
export async function fetchEntryZoneForOpportunities(
  opportunityIds: number[],
): Promise<Record<string, OpportunityEntryZoneFlags>> {
  const map: Record<string, OpportunityEntryZoneFlags> = {}
  const ids = Array.from(new Set(opportunityIds.filter((id) => Number.isFinite(id))))
  if (ids.length === 0) return map

  const { data, error } = await supabase
    .rpc('compute_entry_zone_for_opportunities', { p_ids: ids })

  if (error) {
    console.error('compute_entry_zone_for_opportunities error', error)
    return map
  }

  const rows = Array.isArray(data) ? data : []

  for (const row of rows as any[]) {
    if (!row) continue
    const key = String(row.opportunity_id)
    map[key] = {
      hasEntryZone: !!row.has_entry_zone,
      inEntryZone: !!row.in_entry_zone,
    }
  }

  return map
}

// Resultados consolidados (TP1/TP2/TP3/Stop) para oportunidades encerradas
export async function fetchSignalsOutcomesForOpportunities(
  opportunityIds: number[],
): Promise<Record<string, OpportunitySignalsOutcome>> {
  const map: Record<string, OpportunitySignalsOutcome> = {}
  const ids = Array.from(new Set(opportunityIds.filter((id) => Number.isFinite(id))))
  if (ids.length === 0) return map

  const { data, error } = await supabase
    .rpc('get_signals_outcomes_for_opportunities', { p_ids: ids })

  if (error) {
    console.error('get_signals_outcomes_for_opportunities error', error)
    return map
  }

  const rows = Array.isArray(data) ? data : []

  for (const row of rows as any[]) {
    if (!row) continue
    const key = String(row.opportunity_id)
    const resultType = String(row.result_type) as OpportunitySignalsOutcome['resultType']
    map[key] = {
      opportunityId: row.opportunity_id,
      resultType,
      tp1Hit: !!row.tp1_hit,
      tp2Hit: !!row.tp2_hit,
      tp3Hit: !!row.tp3_hit,
      stopHit: !!row.stop_hit,
      entryMin: row.entry_min ?? null,
      entryMax: row.entry_max ?? null,
      entryMid: row.entry_mid ?? null,
      stopPrice: row.stop_price ?? null,
      tp1: row.tp1 ?? null,
      tp2: row.tp2 ?? null,
      tp3: row.tp3 ?? null,
      closedAt: row.closed_at,
    }
  }

  return map
}

// Versão batch com progresso (usada apenas em fluxos específicos / debug)
export async function fetchSignalsFullForOpportunities(
  opportunityIds: number[],
): Promise<Record<string, OpportunitySignals>> {
  const map: Record<string, OpportunitySignals> = {}
  const ids = Array.from(new Set(opportunityIds.filter((id) => Number.isFinite(id))))
  if (ids.length === 0) return map

  const { data, error } = await supabase
    .rpc('compute_signals_full_for_opportunities', { p_ids: ids })

  if (error) {
    console.error('compute_signals_full_for_opportunities error', error)
    return map
  }

  const rows = Array.isArray(data) ? data : []

  for (const row of rows as any[]) {
    if (!row) continue
    const key = String(row.opportunity_id)
    map[key] = {
      opportunityId: row.opportunity_id,
      side: row.side,
      tfMandante: row.tf_mandante,
      tfSuperior: row.tf_superior ?? null,
      entryMin: row.entry_min,
      entryMax: row.entry_max,
      entryMid: row.entry_mid,
      stopPrice: row.stop_price,
      stopAggressive: row.stop_aggressive ?? row.stop_price ?? null,
      stopConservative: row.stop_conservative ?? row.stop_price ?? null,
      tp1: row.tp1,
      tp2: row.tp2,
      tp3: row.tp3,
      tp1Hit: typeof row.tp1_hit === 'boolean' ? row.tp1_hit : null,
      tp2Hit: typeof row.tp2_hit === 'boolean' ? row.tp2_hit : null,
      tp3Hit: typeof row.tp3_hit === 'boolean' ? row.tp3_hit : null,
      stopHit: typeof row.stop_hit === 'boolean' ? row.stop_hit : null,
    }
  }

  return map
}
